const defectTypeKeys = {
    inspection: "Inspection",
    event: "Event",
    check: "Check",
    tyreCheck: "TyreCheck"
};

// Tokens the report page reads from its link to decide where its back button returns to.
const returnPages = {
    fleetDefects: "fleet-defects",
    assetDefects: "asset-defects",
    fleetEvents: "fleet-events",
    assetEvents: "asset-events",
    fleetPlanner: "fleet-planner"
};

const defectTypeKeysByName = {
    "Tyre Check": defectTypeKeys.tyreCheck
};

function isInspectionDefect(defect) {
    return defect?.defectTypeKey === defectTypeKeys.inspection;
}

// Stored list filters may hold a defect type's display name; the search takes its key.
function getDefectTypeKey(defectTypeValue) {
    return defectTypeKeysByName[defectTypeValue] ?? defectTypeValue;
}

// A defect from a multi-vehicle visit opens the visit summary, which shows every vehicle's report; any other defect opens
// its own report. A search row carries the report id on its parent; the single-defect read carries it on the defect.
// No URL when the defect says the user cannot open its report; a defect that does not say keeps its link.
function getReportBaseUrl(defect) {
    if (defect?.reportAvailable === false) {
        return null;
    }
    if (defect?.summaryContainerId) {
        return `${window.env.VISION_URL}/#/WorkItemReport/WorkItemSummary/${defect.summaryContainerId}`;
    }
    const reportId = defect?.reportId ?? defect?.parent?.reportId;
    if (!reportId) {
        return null;
    }
    return `${window.env.VISION_URL}/#/WorkItemReport/${reportId}`;
}

// Adds the query string only when there is at least one parameter. The return context ({ returnTo, assetId }) tells
// the report page where its back button returns to.
function withQuery(url, { defectId = null, tab = null, returnContext = null }) {
    const params = new URLSearchParams();
    if (defectId != null) {
        params.append("defectId", defectId);
    }
    if (tab) {
        params.append("tab", tab);
    }
    if (returnContext?.returnTo) {
        params.append("returnTo", returnContext.returnTo);
    }
    if (returnContext?.assetId != null) {
        params.append("assetId", returnContext.assetId);
    }
    const query = params.toString();
    return query ? `${url}?${query}` : url;
}

function getInspectionReportUrl(defect, returnContext = null) {
    const reportUrl = getReportBaseUrl(defect);
    return reportUrl ? withQuery(reportUrl, { returnContext }) : null;
}

// The legacy report opened at the defect; the report alone when the defect's legacy id is not known.
function getInspectionDefectUrl(defect, returnContext = null) {
    const reportUrl = getReportBaseUrl(defect);
    if (!reportUrl) {
        return null;
    }
    return withQuery(reportUrl, { defectId: defect.inspectionDefectId, returnContext });
}

// The legacy report's Defects tab opened at the defect, where the defect is signed off. The legacy page opens the
// report at the defect instead when the user cannot see that tab.
function getInspectionDefectsTabUrl(defect, returnContext = null) {
    const reportUrl = getReportBaseUrl(defect);
    if (!reportUrl || defect.inspectionDefectId == null) {
        return null;
    }
    return withQuery(reportUrl, { defectId: defect.inspectionDefectId, tab: "defects", returnContext });
}

// Decides how a defect's status can be updated: an inspection defect links to the legacy Defects tab at that defect,
// any other source opens the sign-off dialog until it is rectified. Returns null when no update is offered.
function getUpdateStatusAction(defect, canUpdateStatus, canOpenInspectionReport, returnContext = null) {
    if (!canUpdateStatus(defect)) {
        return null;
    }

    if (isInspectionDefect(defect)) {
        const href = canOpenInspectionReport ? getInspectionDefectsTabUrl(defect, returnContext) : null;
        return href ? { href } : null;
    }

    if (defect.rectified) {
        return null;
    }

    return { signoff: true };
}

export const DefectHelperFunctions = {
    defectTypeKeys,
    returnPages,
    isInspectionDefect,
    getDefectTypeKey,
    getInspectionReportUrl,
    getInspectionDefectUrl,
    getInspectionDefectsTabUrl,
    getUpdateStatusAction
};
